/*
 * WCBJ native infinite scroll for collection/search product grids.
 * Replaces the Scrollio app with a dependency-free, progressive-enhancement
 * version. If JS is disabled or this errors, the normal numbered pagination
 * (which we only hide via JS) keeps working.
 *
 * Keys off Dawn's standard markup:
 *   #ProductGridContainer  (survives Dawn facet/sort re-renders)
 *     #product-grid        (the <ul> of product <li> cards)
 *     .pagination-wrapper  (numbered pagination; source of "next" URL)
 *
 * NOTE: this theme's snippets/pagination.liquid has the --next / --prev classes
 * swapped, so we derive the next URL from the highest page= number rather than
 * from those class names (also filter- and locale-safe).
 */
(function () {
  'use strict';

  var CONTAINER = '#ProductGridContainer';
  var GRID = '#product-grid';
  var WRAPPER = '.pagination-wrapper';

  function pageOf(href) {
    try {
      var u = new URL(href, window.location.origin);
      return parseInt(u.searchParams.get('page') || '1', 10);
    } catch (e) {
      return null;
    }
  }

  // From a pagination wrapper, return {url, page} of the smallest page number
  // strictly greater than currentPage, or null if none.
  function nextFrom(wrapper, currentPage) {
    if (!wrapper) return null;
    var links = wrapper.querySelectorAll('.pagination__list a[href]');
    var best = null;
    var bestPage = Infinity;
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href');
      var pg = pageOf(href);
      if (pg !== null && pg > currentPage && pg < bestPage) {
        bestPage = pg;
        var u = new URL(href, window.location.origin);
        best = u.pathname + u.search;
      }
    }
    return best ? { url: best, page: bestPage } : null;
  }

  function currentPageFromLocation() {
    return pageOf(window.location.href) || 1;
  }

  function InfiniteScroll(container) {
    this.container = container;
    this.grid = container.querySelector(GRID);
    this.wrapper = container.querySelector(WRAPPER);
    this.loading = false;
    this.io = null;
    this.sentinel = null;
    this.loader = null;

    if (!this.grid || !this.wrapper) return;

    var next = nextFrom(this.wrapper, currentPageFromLocation());
    if (!next) return; // already on the last page

    this.next = next;
    // Hide numbered pagination (kept in markup as the no-JS fallback).
    this.wrapper.setAttribute('hidden', '');
    this._build();
    this._observe();
  }

  InfiniteScroll.prototype._build = function () {
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll__sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    // Dawn's base.css hides empty block elements: `div:empty { display: none }`.
    // A display:none element never triggers an IntersectionObserver, so the
    // sentinel MUST have a child to stay rendered. (span isn't in that rule's
    // selector list, so an empty span child is safe and invisible.)
    this.sentinel.appendChild(document.createElement('span'));

    this.loader = document.createElement('div');
    this.loader.className = 'infinite-scroll__loader';
    this.loader.hidden = true;
    this.loader.innerHTML =
      '<span class="infinite-scroll__spinner" aria-hidden="true"></span>' +
      '<span class="visually-hidden">Loading more products</span>';

    this.grid.insertAdjacentElement('afterend', this.sentinel);
    this.sentinel.insertAdjacentElement('afterend', this.loader);
  };

  InfiniteScroll.prototype._observe = function () {
    var self = this;
    this.io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            self._loadNext();
            break;
          }
        }
      },
      { rootMargin: '800px 0px' }
    );
    this.io.observe(this.sentinel);
  };

  InfiniteScroll.prototype._loadNext = function () {
    if (this.loading || !this.next) return;
    var self = this;
    this.loading = true;
    this.loader.hidden = false;

    fetch(this.next.url)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector(GRID);
        if (newGrid) {
          var frag = document.createDocumentFragment();
          var kids = Array.prototype.slice.call(newGrid.children);
          for (var i = 0; i < kids.length; i++) {
            frag.appendChild(document.importNode(kids[i], true));
          }
          self.grid.appendChild(frag);
        }
        // Derive the following page from the just-fetched document. We
        // deliberately do NOT rewrite the address bar: leaving it on the
        // collection URL means a reload returns to the top (page 1) and
        // scroll resumes, rather than dropping the shopper onto a middle page.
        self.next = nextFrom(doc.querySelector(WRAPPER), self.next.page);
      })
      .catch(function () {
        // On failure, restore normal pagination and stop enhancing.
        if (self.wrapper) self.wrapper.removeAttribute('hidden');
        self.next = null;
        self._teardown();
      })
      .then(function () {
        self.loading = false;
        if (self.loader) self.loader.hidden = true;
        if (!self.next) {
          self._showEnd();
          self._teardown();
          return;
        }
        // IntersectionObserver only fires on a *change* in intersection, so it
        // can miss the case where the sentinel is still in view after a load
        // (e.g. the shopper is parked at the bottom). Explicitly continue while
        // the sentinel remains in the preload zone; otherwise the observer picks
        // it up on the next scroll.
        if (self._sentinelInView()) {
          setTimeout(function () { self._loadNext(); }, 150);
        }
      });
  };

  InfiniteScroll.prototype._sentinelInView = function () {
    if (!this.sentinel) return false;
    var r = this.sentinel.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top <= vh + 800 && r.bottom >= 0;
  };

  InfiniteScroll.prototype._showEnd = function () {
    if (!this.grid) return;
    // insert an end marker after the grid so the shopper knows the list is complete
    var end = document.createElement('p');
    end.className = 'infinite-scroll__end';
    end.setAttribute('role', 'status');
    end.textContent = "You've reached the end of the catalog.";
    this.grid.insertAdjacentElement('afterend', end);
  };

  InfiniteScroll.prototype._teardown = function () {
    if (this.io) this.io.disconnect();
    if (this.sentinel) this.sentinel.remove();
    if (this.loader) this.loader.remove();
    this.io = this.sentinel = this.loader = null;
  };

  // ---- bootstrap + re-init after Dawn facet/sort re-renders ----
  var instance = null;
  var rebuildTimer = null;

  function init() {
    var container = document.querySelector(CONTAINER);
    if (!container) return;
    if (instance) instance._teardown();
    instance = new InfiniteScroll(container);

    // Dawn's FacetFiltersForm replaces the grid markup inside the container on
    // filter/sort. Watch for that and re-init (debounced).
    if (!container.__wcbjISObserved) {
      container.__wcbjISObserved = true;
      // subtree:false is deliberate — Dawn's facet/sort re-render replaces the
      // container's DIRECT children, which we want to react to; our own card
      // appends happen deeper (on #product-grid), which must NOT retrigger this.
      var mo = new MutationObserver(function () {
        clearTimeout(rebuildTimer);
        rebuildTimer = setTimeout(init, 250);
      });
      mo.observe(container, { childList: true, subtree: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
